import { useState } from 'react';

import { useNotifications } from 'modules/app/notifications';
import { SupplierEditForm } from 'modules/suppliers/components';
import { useFetchSupplier } from 'modules/suppliers/hooks';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

import { useTranslation } from '../../hooks/useTranslation';

type Supplier = { id: string | undefined; name: string; url?: string };

type ExpenditurePanelHeaderSupplierFormProps = {
  supplier: { id: string | undefined; name: string; url?: string } | undefined;
  onCancel: () => void;
  onSubmit: (newSupplier: Pick<Supplier, 'id' | 'name'>) => Promise<void>;
  isSupplierEditable?: boolean;
  isSupplierDetailsEditable?: boolean;
  withSupplierDprInformation?: boolean;
  shouldClearSpainClosureErrors?: boolean;
  canCreateSupplier?: boolean;
};

export const ExpenditurePanelHeaderSupplierForm = ({
  supplier,
  onSubmit,
  onCancel,
  isSupplierEditable,
  isSupplierDetailsEditable,
  withSupplierDprInformation,
  shouldClearSpainClosureErrors,
  canCreateSupplier,
}: ExpenditurePanelHeaderSupplierFormProps) => {
  const [internalSupplier, setInternalSupplier] = useState<
    Pick<Supplier, 'id' | 'name'> | undefined
  >(supplier);
  const { t } = useTranslation('global');
  const { successNotif } = useNotifications();

  const fetchSupplierQuery = useFetchSupplier(
    isSupplierDetailsEditable ? internalSupplier?.id : undefined,
  );
  const supplierData = unwrapQuery(fetchSupplierQuery);
  const supplierDetails = supplierData && {
    vatNumber: supplierData.supplierDetails?.legalInfos?.vatNumber,
    country: supplierData.supplierDetails?.address?.country,
  };

  return (
    <SupplierEditForm
      supplier={{
        ...internalSupplier,
        id: internalSupplier?.id ?? '',
        name: internalSupplier?.name ?? '',
        vatNumber: supplierDetails?.vatNumber ?? undefined,
        country: supplierDetails?.country ?? undefined,
      }}
      canCreateSupplier={canCreateSupplier}
      onSupplierSelected={async (newSupplier, confirmRightAfter) => {
        // Idempotency: do not update if supplier is the same
        if (!newSupplier || newSupplier.id === internalSupplier?.id) {
          return;
        }

        if (confirmRightAfter) {
          await onSubmit(newSupplier);
          successNotif(t('suppliers.updateSuccess'));
        }
        setInternalSupplier(newSupplier);
      }}
      onConfirm={() => internalSupplier && onSubmit(internalSupplier)}
      onCancel={onCancel}
      shouldDisplayDPRInformations={withSupplierDprInformation}
      shouldClearSpainClosureErrors={shouldClearSpainClosureErrors}
      isSupplierEditable={isSupplierEditable}
    />
  );
};
